import { useCallback, useEffect } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { CalendarDays, Undo2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useLastChart } from '@/stores/lastChart';
import { firstNameOf, lastNameOf } from '@/utils/patientName';

/** Alt + esta tecla física alterna Agenda ⇄ Ficha (Alt+A painel, Alt+Z foco). */
const TOGGLE_CODE = 'KeyX';

/**
 * Pra onde o Agenda ⇄ Ficha leva agora: na ficha, à agenda; fora dela, à
 * última ficha aberta HOJE nesta máquina. `target` null = não há pra onde ir.
 * Também serve ao item do "⋯" da barra do topo quando o botão não cabe.
 */
export const useAgendaChartTarget = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { clinicId } = useAuth();
  const chart = useLastChart(clinicId);

  const openId = matchPath('/pacientes/:id', pathname)?.params.id ?? null;
  const onChart = openId !== null;
  const target = onChart ? '/agenda' : chart ? `/pacientes/${chart.patientId}` : null;

  const go = useCallback(() => {
    if (target) navigate(target);
  }, [navigate, target]);

  const first = chart ? firstNameOf(chart.name) : '';
  const last = chart ? lastNameOf(chart.name) : '';
  const shortName = chart ? (last && last !== first ? `${first} ${last}` : first || chart.name) : '';

  return { target, onChart, go, chartName: chart?.name ?? '', shortName };
};

/**
 * Botão do topo que alterna entre a ficha do paciente em atendimento e a
 * Agenda completa. Na ficha ele leva à agenda; fora dela (agenda ou qualquer
 * outra tela) volta à última ficha aberta HOJE nesta máquina — o paciente
 * em atendimento. Sem ficha aberta hoje, fora da ficha, ele some.
 * `hidden`: o botão foi pro "⋯" da barra, mas o Alt+X continua valendo.
 */
export const AgendaChartToggle = ({ hidden = false }: { hidden?: boolean }) => {
  const { target, onChart, go, chartName, shortName } = useAgendaChartTarget();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey && !e.ctrlKey && !e.metaKey && e.code === TOGGLE_CODE) {
        e.preventDefault();
        go();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go]);

  if (!target || hidden) return null;

  return (
    <button
      type="button"
      onClick={go}
      data-dock-control
      className="hidden md:inline-flex h-9 max-w-[15rem] items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3.5 text-sm font-medium text-primary transition-colors hover:bg-primary/15"
      title={onChart
        ? 'Ir para a agenda completa (Alt+X)'
        : `Voltar para a ficha de ${chartName} (Alt+X)`}
      aria-label={onChart ? 'Ir para a agenda' : `Voltar para a ficha de ${chartName}`}
    >
      {onChart ? (
        <><CalendarDays size={16} className="shrink-0" /> Agenda</>
      ) : (
        <><Undo2 size={16} className="shrink-0" /><span className="truncate">{shortName}</span></>
      )}
    </button>
  );
};
